import { DownloadOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Button, Card, DatePicker, Form, Space, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import CopyableText from '../../components/copyable-text';
import DeviceDetails from '../../components/device-details';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Device } from '../../types/device.types';
import { RetailerMinimal } from '../../types/retailers.types';
import { UserMinimal } from '../../types/user.types';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;

interface FilterValues {
	status: string | null;
	phone_number: string | null;
	retailer: number | null;
	user: number | null;
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const InsuranceReport = () => {
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		status: null,
		phone_number: null,
		retailer: null,
		user: null,
		date_range: null,
	});
	const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
	const [isExporting, setIsExporting] = useState(false);

	const { data, error, isLoading } = useQuery({
		queryKey: ['devices', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				status: 'approved',
				phone_number: filters.phone_number,
				retailer: filters.retailer,
				user: filters.user,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};

			const response = await axios.get('/admin/devices', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const devices: Device[] = data?.results || [];

	const columns: ColumnsType<Device> = [
		{
			title: 'Device Description',
			dataIndex: 'phone_model',
			key: 'phone_model',
			render: (phone_model: string) => phone_model || 'N/A',
		},
		{
			title: 'IMEI (Device Serial Number)',
			dataIndex: 'imei',
			key: 'imei',
			render: (imei: string) => (
				<CopyableText text={imei}>
					<span className="font-mono text-sm">{imei}</span>
				</CopyableText>
			),
		},
		{
			title: 'Device Value',
			dataIndex: 'phone_amount',
			key: 'phone_amount',
			render: (phone_amount: string) => `${formatNumber(phone_amount)} UGX`,
		},
		{
			title: 'Retailer Name',
			dataIndex: 'sold_by',
			key: 'sold_by_name',
			render: (sold_by: RetailerMinimal) => sold_by.business_name,
		},
		{
			title: 'Retailer ID',
			dataIndex: 'sold_by',
			key: 'retailer_id',
			render: (sold_by: RetailerMinimal) => (
				<CopyableText text={sold_by.vendor_id}>
					<span className="font-mono text-sm">{sold_by.vendor_id}</span>
				</CopyableText>
			),
		},
		{
			title: 'Customer Name',
			dataIndex: 'user',
			key: 'customer_name',
			render: (user: UserMinimal) => (
				<div className="flex items-center gap-2">
					<Avatar
						src={user.dp}
						size="small"
					/>
					<span className="text-sm font-medium">
						{user.first_name} {user.last_name}
					</span>
				</div>
			),
		},
		{
			title: 'Customer ID',
			dataIndex: 'user',
			key: 'customer_id',
			render: (user: UserMinimal) => <CopyableText text={user.id.toString()}></CopyableText>,
		},
		{
			title: 'Customer Address',
			dataIndex: 'user',
			key: 'customer_address',
			render: (user: UserMinimal) => (
				<span className="capitalize italic text-gray-500">
					District: {user.district}, Town: {user.town}
				</span>
			),
		},
		{
			title: 'Initial Deposit',
			dataIndex: 'cash_in_hand',
			key: 'initial_deposit',
			render: (cash_in_hand: string) => `${formatNumber(cash_in_hand)} UGX`,
		},
		{
			title: 'Repayment Period',
			dataIndex: 'duration',
			key: 'repayment_period',
			render: (duration: number, device: Device) => (
				<span className="capitalize">{`${duration} ${device.duration_unit}${duration > 1 ? 's' : ''}`}</span>
			),
		},
	];

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			status: null,
			phone_number: null,
			retailer: null,
			user: null,
			date_range: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const handleExcelExport = async () => {
		try {
			setIsExporting(true);

			// Use the current page devices that are already loaded
			const devicesToExport = devices;

			if (devicesToExport.length === 0) {
				message.warning('No devices to export on the current page.');
				return;
			}

			// Prepare data for Excel export
			const exportDataFormatted = devicesToExport.map((device, index) => ({
				'S/N': index + 1,
				'Device Description': device.phone_model || 'N/A',
				'IMEI (Device Serial Number)': device.imei,
				'Device Value (UGX)': device.phone_amount,
				'Retailer Name': device.sold_by?.business_name || 'N/A',
				'Retailer ID': device.sold_by?.vendor_id || 'N/A',
				'Customer Name': `${device.user?.first_name || ''} ${device.user?.last_name || ''}`.trim(),
				'Customer ID': device.user?.id || 'N/A',
				'Customer District': device.user?.district || 'N/A',
				'Customer Town': device.user?.town || 'N/A',
				'Initial Deposit (UGX)': device.cash_in_hand,
				'Repayment Period': `${device.duration} ${device.duration_unit}${device.duration > 1 ? 's' : ''}`,
			}));

			// Create workbook and worksheet
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.json_to_sheet(exportDataFormatted);

			// Set column widths for better readability
			const colWidths = [
				{ wch: 5 }, // S/N
				{ wch: 20 }, // Device Description
				{ wch: 18 }, // IMEI
				{ wch: 15 }, // Device Value
				{ wch: 20 }, // Retailer Name
				{ wch: 12 }, // Retailer ID
				{ wch: 20 }, // Customer Name
				{ wch: 12 }, // Customer ID
				{ wch: 15 }, // Customer District
				{ wch: 15 }, // Customer Town
				{ wch: 18 }, // Initial Deposit
				{ wch: 18 }, // Repayment Period
			];
			ws['!cols'] = colWidths;

			// Add worksheet to workbook
			XLSX.utils.book_append_sheet(wb, ws, 'Insurance Report');

			// Generate filename with current date and pagination info
			const today = dayjs().format('YYYY-MM-DD');
			const dateRangeStr = filters.date_range
				? `_${filters.date_range[0].format('YYYY-MM-DD')}_to_${filters.date_range[1].format('YYYY-MM-DD')}`
				: '';
			const pageInfo = `_Page${paginationConfig.page}_Size${paginationConfig.pageSize}`;
			const filename = `Insurance_Report_${today}${dateRangeStr}${pageInfo}.xlsx`;

			// Save file
			XLSX.writeFile(wb, filename);

			message.success(
				`Successfully exported ${devicesToExport.length} devices from current page (Page ${paginationConfig.page}, Size: ${paginationConfig.pageSize}) to Excel file: ${filename}`,
			);
		} catch (error) {
			console.error('Export error:', error);
			message.error('Failed to export data. Please try again.');
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<AppLayout
			title="Reports"
			subtitle="Insurance Report"
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						{/* <Form.Item
							name="phone_number"
							label="Phone Number"
							className="mb-0 min-w-[180px]"
						>
							<Input
								placeholder="Enter phone number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="retailer"
							label="Retailer ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="Retailer ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="user"
							label="User ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="User ID"
								type="number"
								allowClear
							/>
						</Form.Item> */}

						<Form.Item
							name="date_range"
							label="Date Range"
							className="mb-0 min-w-[300px]"
						>
							<RangePicker
								allowClear
								format="YYYY-MM-DD"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
								<Button
									onClick={handleExcelExport}
									loading={isExporting}
									icon={<DownloadOutlined />}
									type="default"
								>
									Export to Excel
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={devices}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>

			{selectedDevice && (
				<DeviceDetails
					device={selectedDevice}
					onClose={() => setSelectedDevice(null)}
				/>
			)}
		</AppLayout>
	);
};

export default InsuranceReport;
